import { useState } from 'react'
import { RowMapping, type Spec } from '../components/RowMapping'
import { UnmatchedPortfolios } from '../components/UnmatchedPortfolios'
import { api, type Divergence, type ParseResult, type Upload } from '../api'
import { Promote } from '../components/Promote'
import { formatDelimited, parseDelimited } from '../csv'
import { says } from '../limits'
import { DeskCell, DeskHeader, useDesks } from '../desks'
import { sortRows, useSort } from '../components/Grid'

/**
 * The migration screen — TECHNICAL_DESIGN.md §7.1 stages 1 and 2.
 *
 * *"Import is a three-stage review, not a load."* The screen is built in those
 * three stages and will not let you skip one: parse, reconcile, stage. Sign-off
 * is stage three and lives on the approval screen, deliberately — a migration UI
 * that could approve its own output would be the load this section rules out.
 *
 * **The parse result leads with what it could not structure.** Most of a real
 * mandate grid is prose — measured against the customer's own workbook, 12 of
 * 120 populated cells parse to a threshold — and an operator who sees only the
 * successes will conclude the import worked. The narrative count and the
 * unmapped rows are the first thing on the screen, because they are the part
 * that needs a decision.
 *
 * **Dual keying is a second paste, not a checkbox.** §7.1 requires two operators
 * to key the numbers independently, and the only way a screen can require that
 * is to ask for the second sheet. The stage button stays disabled until a
 * reconciliation has run clean; a control that can be dismissed on a busy
 * afternoon is not a control.
 *
 * **Promotion candidates are found here and promoted deliberately.** The import
 * never promotes anything on its own — twelve columns reading 2% may be one house
 * policy or twelve independent negotiations, and the workbook cannot tell you
 * which. A missed promotion leaves the store no worse than the spreadsheet; a
 * wrong one imposes a limit one client negotiated on portfolios whose mandates
 * never contained it.
 *
 * What changed is that the act became possible. There was no promote control
 * anywhere in the product and `api.propose` had no callers at all, so the whole
 * scope layer — groups, membership, the blast radius echoed back at approval,
 * every resolved rule tagged with the group it came through — was machinery
 * serving a rule type nothing could create. `Promote` is that act, and it keeps
 * every refusal the store makes: an approver's capability, a document that
 * authorises it, the portal channel, and an approval afterwards where §5.6 shows
 * which funds it reaches.
 */
type Stage = 'paste' | 'parsed' | 'reconciled'

/** What somebody does, in the order the work happens.
 *
 * **The checking step only exists when somebody typed the numbers.** Read out of
 * a file, there is nothing to check them against but the machine's own reading —
 * so the step is absent rather than optional. A step people are walked through
 * and told they may skip is a step that teaches them to skip steps. */
const ALL_STEPS = [
  { id: 'load', title: 'Load the workbook' },
  { id: 'map', title: 'Say what the rows mean' },
  { id: 'parse', title: 'Read it' },
  { id: 'check', title: 'Check the numbers', onlyWhenTyped: true },
  { id: 'stage', title: 'Hand it to an approver' },
] as const

type Step = (typeof ALL_STEPS)[number]['id']

/**
 * The pasted grid, quoting and all — see `csv.ts`.
 *
 * This used to split on `,` under a comment saying a quoted comma "is the one
 * case this gets wrong" and that reconciliation would catch it. It cannot:
 * §7.1's second keying compares two pastes through *this same function*, so
 * both mis-split identically and the comparison reports clean.
 */
function toRows(text: string): string[][] {
  return parseDelimited(text)
}

export function Migration({
  canCreatePortfolios,
  canAttachToScope = false,
}: {
  canCreatePortfolios: boolean
  /** §5.7's promotion is an approver's act — it puts a limit on every fund in a
   *  group. The control is not shown to somebody the store would refuse. */
  canAttachToScope?: boolean
}) {
  // Chosen from the sheet's own row labels and the dictionary's own terms,
  // rather than typed as JSON — see `RowMapping`.
  const [rowMap, setRowMap] = useState<Record<string, Spec>>({})
  const [first, setFirst] = useState('')
  const [second, setSecond] = useState('')
  const [stage, setStage] = useState<Stage>('paste')
  const [step, setStep] = useState<Step>('load')
  const [parsed, setParsed] = useState<ParseResult | null>(null)
  const [divergences, setDivergences] = useState<Divergence[] | null>(null)
  const [staged, setStaged] = useState<string | null>(null)
  // Today, as a date the operator can see and change — not `new Date()` read off
  // the clock at the moment they press the button.
  const [effectiveFrom, setEffectiveFrom] = useState(
    () => new Date().toISOString().slice(0, 10),
  )
  /** Rows the store will not decide for us. §7.1: whether a candidate amends an
   *  active limit or joins it is answered in the amending document, not in the
   *  data — so it is asked, and the rest of the file still lands. */
  /** Limits in force that this workbook no longer contains (decision 72). */
  const [dropped, setDropped] = useState<{ rule_id: string; rule_key: string; portfolio_code: string }[]>([])
  /** The dropped limits' Desk column, for somebody across the whole firm (decision 91). */
  const [droppedDesk, setDroppedDesk] = useState('')
  const droppedSort = useSort()
  const directory = useDesks()
  const [undecided, setUndecided] = useState<
    { ref: string; rule_key: string; reason: string }[]
  >([])
  const [upload, setUpload] = useState<Upload | null>(null)
  const [error, setError] = useState<string | null>(null)

  // The first column of the grid — every row the workbook holds, whether it was
  // uploaded or pasted.

  // Numbers lifted out of a file by the machine, rather than typed by a person.
  // A second keying compares a reading with itself there, so it is offered and
  // not required — see `migration.stage`.
  const readFromFile = upload !== null
  const STEPS = ALL_STEPS.filter((s) => !('onlyWhenTyped' in s && s.onlyWhenTyped) || !readFromFile)
  const at = Math.max(0, STEPS.findIndex((s) => s.id === step))

  /** Why the next step is not reachable yet, in the words to say so in. */
  const blocked: string | null =
    step === 'load' && !first.trim()
      ? 'load a workbook first'
      : step === 'parse' && !parsed
        ? 'read the workbook first'
        : step === 'check' && stage !== 'reconciled'
          ? 'the two sets of numbers have to agree first'
          : null

  function forward() {
    if (blocked) return
    setStep(STEPS[Math.min(STEPS.length - 1, at + 1)]!.id)
  }

  const rowLabels = toRows(first)
    .slice(1)
    .map((row: string[]) => row[0]?.trim() ?? '')
    .filter(Boolean)

  async function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    setError(null)
    try {
      const result = await api.migrationUpload(file)
      setUpload(result)
      // The server re-cut the sheet, so the paste box is filled with the grid
      // the parser expects rather than the operator having to find the table.
      //
      // Quoted on the way back in. This was `row.join(',')`, which is right for
      // every cell holding no comma and destroys the ones that do: the server
      // read `"Yes, monthly"` correctly out of the file, the client wrote it
      // back as `Yes, monthly`, and the next parse split it in two and shifted
      // every column after it. The file was never the problem, and a parser that
      // handles quoting cannot save a string the quoting has been stripped from.
      setFirst(formatDelimited(result.rows))
      if (result.known_profile) {
        // A shape somebody has already explained: its mapping is loaded rather
        // than asked for again.
        setRowMap(result.known_profile.row_map as Record<string, Spec>)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  /** The mapping, as the parser wants it. No parsing step any more: it is built
   *  by choosing from the sheet's rows and the dictionary's terms. */
  function profile(): Record<string, unknown> {
    return rowMap
  }

  /**
   * Where the pasted grid sits in the uploaded sheet, so each draft's cell
   * reference is one the operator can open the workbook and check.
   *
   * Undefined when the grid was typed rather than uploaded — there is no sheet
   * to be relative to, and the reference is the grid's own coordinates, which is
   * the honest answer in that case.
   */
  function where() {
    return upload
      ? { column_refs: upload.column_refs, sheet_offset: upload.sheet_offset }
      : undefined
  }

  async function doParse() {
    setError(null)
    try {
      setParsed(await api.migrationParse(toRows(first), profile(), where()))
      setDivergences(null)
      setStage('parsed')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function doReconcile() {
    setError(null)
    try {
      const found = await api.migrationReconcile(
        toRows(first),
        toRows(second),
        profile(),
        where(),
      )
      setDivergences(found)
      setStage(found.length === 0 ? 'reconciled' : 'parsed')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function doStage() {
    if (!parsed) return
    setError(null)
    try {
      const refs = parsed.drafts.filter((d) => d.structured).map((d) => d.ref)
      const result = await api.migrationStage(
        toRows(first),
        profile(),
        // The date the migrator chose, not the moment they pressed the button.
        // This was `new Date()` with nothing on the screen to change it, so a
        // workbook of limits agreed in April and imported in August became
        // limits that took effect in August — every one of them, silently.
        new Date(`${effectiveFrom}T00:00:00Z`).toISOString(),
        refs,
        where(),
        readFromFile ? 'read' : 'keyed',
        // The workbook itself, so every proposal out of it cites the file it
        // came from and an approver has something to open. Absent when the rows
        // were pasted rather than uploaded — there is no file to cite then, and
        // those proposals wait for clause review exactly as they always did.
        upload?.pack ?? null,
      )
      setStaged(
        `${result.staged} proposal(s) staged. ` +
          (result.pack_document_id
            ? 'The workbook is now on Mandate documents as a guideline pack, and ' +
              'every one of these cites it — which is where it came from, not the ' +
              'contract that justifies it. Clause review is where they get that. '
            : '') +
          // Re-importing a workbook is ordinary, and the rows it says nothing
          // new about are most of it. Counted rather than dropped: "12 staged"
          // out of 52 rows leaves the operator to work out what happened to the
          // other forty.
          (result.unchanged.length
            ? `${result.unchanged.length} row(s) already in force, unchanged — nothing proposed for those. `
            : '') +
          (result.already_proposed?.length
            ? `${result.already_proposed.length} row(s) already waiting for an approver as proposed — nothing proposed again. `
            : '') +
          (result.facts_recorded
            ? `${result.facts_recorded} fact(s) about the funds recorded, such as the benchmark, and shown on Lookup. `
            : '') +
          (result.blocked_on_vocabulary.length
            ? `${result.blocked_on_vocabulary.length} held back until an approver defines the vocabulary they need. `
            : '') +
          (result.needs_decision.length
            ? `${result.needs_decision.length} held back, listed below with what each needs. `
            : '') +
          (result.unknown_portfolios.length
            ? `Unknown portfolios: ${result.unknown_portfolios.join(', ')}.`
            : ''),
      )
      setUndecided(result.needs_decision)
      setDropped(result.not_in_file ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="migration">
      <header className="page-head">
        <h2>Manually upload guidelines</h2>
        <p className="note">
          Five steps. Nothing is in force at the end of them — what comes out is a
          set of proposed limits for an approver to decide on.
        </p>
      </header>

      <ol className="steps" aria-label="Progress">
        {STEPS.map((s, i) => (
          <li
            key={s.id}
            className={
              s.id === step ? 'here' : STEPS.findIndex((x) => x.id === step) > i ? 'done' : ''
            }
            aria-current={s.id === step ? 'step' : undefined}
          >
            <span className="step-number">{i + 1}</span>
            {s.title}
          </li>
        ))}
      </ol>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {step === 'load' && (<>
      <label>
        Workbook file (.xlsx or .csv)
        <input
          type="file"
          accept=".xlsx,.xlsm,.csv"
          title="The sheet is read here — its shape is worked out and the table filled in below."
          onChange={onFile}
        />
      </label>
      <p className="note">
        Or paste the table straight in, if you have it on screen rather than in a
        file. Either way it appears below and you can correct it before anything
        is read.
      </p>
      <label>
        Paste the table
        <textarea
          rows={8}
          value={first}
          title="The first row is the fund codes; the first column is the row labels."
          onChange={(e) => setFirst(e.target.value)}
        />
      </label>

      {upload && (
        <section
          className={upload.shape.confident ? 'detected' : 'warn'}
          data-testid="detected"
        >
          <h3>
            {upload.shape.confident
              ? 'Table found'
              : 'Could not identify the header — confirm it below'}
          </h3>
          <p data-testid="detection-reason">{upload.shape.reason}</p>
          <p>
            Sheet <b>{upload.sheet}</b>
            {upload.sheets.length > 1 && ` (of ${upload.sheets.length})`} · header
            row {upload.shape.header_row + 1} · labels in column{' '}
            {upload.shape.label_column + 1} · {upload.shape.portfolio_codes.length}{' '}
            portfolio column(s)
          </p>
          {upload.known_profile && (
            <p className="done" data-testid="known-profile">
              This shape matches the saved layout <b>{upload.known_profile.name}</b>,
              so it was not re-detected — and its row mapping has been loaded.
            </p>
          )}
          {/* The columns with no fund behind them, offered rather than created
              — see `UnmatchedPortfolios` for why neither silence nor
              auto-creation is the right answer. */}
          <UnmatchedPortfolios
            codes={upload.shape.unmatched_portfolio_codes ?? []}
            canCreate={canCreatePortfolios}
            onCreated={() =>
              setStaged(
                'Funds created. Upload the workbook again so its columns match them.',
              )
            }
          />
          {!upload.shape.confident && (
            <p className="note">
              Detection matches header cells against portfolios already in the
              store. None matched, so the row below is a guess. Create the
              portfolios first, or correct the grid by hand before parsing.
            </p>
          )}
          <table className="preview">
            <tbody>
              {upload.preview.map((row, i) => (
                <tr key={i} className={i === upload.shape.header_row ? 'header' : ''}>
                  {row.map((cell, j) => (
                    <td key={j}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      </>)}

      {step === 'map' && (
        <RowMapping labels={rowLabels} value={rowMap} onChange={setRowMap} />
      )}

      {step === 'parse' && (
        <>
          <label>
            The table, as it will be read
            <textarea
              rows={8}
              value={first}
              title="Correct anything the sheet needed tidying before it is read."
              onChange={(e) => setFirst(e.target.value)}
            />
          </label>
          <div className="actions">
            <button onClick={() => void doParse()} disabled={!first.trim()}>
              Read the workbook
            </button>
          </div>
        </>
      )}

      {parsed && step !== 'load' && step !== 'map' && (
        <section className="parse-result" data-testid="parse-result">
          {/* What could not be structured, first. An operator who sees only the
              successes will conclude the import worked. */}
          <h3>
            {parsed.narrative_count} of {parsed.drafts.length} cells could not be
            structured
          </h3>
          <p className="note">
            They import as narrative — searchable, owned and citable from day one
            — and appear in the structuring backlog, where a negotiated term is kept
            in its own words until it is structured.
          </p>
          {parsed.unmapped_rows.length > 0 && (
            <p className="warn" data-testid="unmapped">
              Rows not in the profile: {parsed.unmapped_rows.join(', ')}. Each
              imports as narrative. If one of them is a limit you want structured,
              map it and parse again.
            </p>
          )}
          {/* Before anything reaches an approver: a share of NAV over the whole fund
              is the fund measured against itself, and nothing can check it. */}
          {(parsed.uncovered_rows ?? []).length > 0 && (
            <p className="warn" data-testid="uncovered">
              {parsed.uncovered_rows!.join(', ')}: a share of NAV covering the whole fund,
              which is always all of it, so {parsed.uncovered_rows!.length === 1 ? 'it imports' : 'they import'}{' '}
              as text. Go back to step 2 and say which holdings{' '}
              {parsed.uncovered_rows!.length === 1 ? 'it applies' : 'each applies'} to.
            </p>
          )}
          <p data-testid="counts">
            {parsed.structured_count} structured · {parsed.narrative_count} narrative ·{' '}
            {parsed.attributes.length} fund facts
          </p>

          {parsed.promotion_candidates.length > 0 && (
            <div className="candidates" data-testid="promotion-candidates">
              <h4>
                {parsed.promotion_candidates.length} limit(s) appear identically on
                several portfolios
              </h4>
              <p className="note">
                Found, never assumed. Twelve columns reading 2% may be one house
                policy or twelve independent negotiations, and the workbook cannot
                tell you which — so promoting is a separate act that names the
                group and cites the document authorising it, and nothing here does
                it for you.
              </p>
              <table className="grid">
                <thead>
                  <tr>
                    <th>Limit</th>
                    <th>Appears on</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {parsed.promotion_candidates.map((c) => {
                    const said = says(c)
                    return (
                      <tr key={c.refs.join()} data-testid="promotion-candidate">
                        <td>
                          {c.measure ?? c.template}
                          <span className="clause">{said.text}</span>
                        </td>
                        <td>
                          <strong>{c.portfolio_codes.length} funds</strong>
                          <span className="clause">{c.portfolio_codes.join(', ')}</span>
                        </td>
                        <td>
                          <Promote candidate={c} canAttach={canAttachToScope} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {step === 'check' && (<>
          <h3>Check the numbers</h3>
          <p className="note">
            You typed these numbers in, so a second person now types the same ones
            from the original and the two are compared. A mistyped threshold is
            invisible once it is in the system — <strong>15 typed as 1.5 looks
            exactly like a limit somebody meant</strong> — and two people rarely
            make the same mistake. This step is here because the table was typed;
            a workbook loaded as a file skips it, since nobody transcribed
            anything.
          </p>
          <label>
            The second person's numbers
            <textarea
              rows={8}
              value={second}
              title="The same table, typed again by somebody else from the original."
              onChange={(e) => setSecond(e.target.value)}
            />
          </label>
          <button
            onClick={() => void doReconcile()}
            disabled={!second.trim()}
          >
            Compare the two
          </button>


          {divergences && divergences.length > 0 && (
            <div className="warn" data-testid="divergences">
              <h4>{divergences.length} cell(s) disagree</h4>
              <p>Blocked until they are resolved at the source, not here.</p>
              <ul>
                {divergences.map((d) => (
                  <li key={d.ref} data-testid="divergence">
                    <code>{d.ref}</code> {d.row_label} / {d.portfolio_code}:{' '}
                    {d.first ? String(d.first.threshold) : <em>not keyed</em>} vs{' '}
                    {d.second ? String(d.second.threshold) : <em>not keyed</em>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {divergences && divergences.length === 0 && (
            <p className="done" data-testid="reconciled">
              Both keyings agree.
            </p>
          )}
          </>)}

          {step === 'stage' && !staged && (
            <>
              <p className="note">
                {readFromFile
                  ? 'The record will say these numbers were read from your file. Every one carries the cell it came from, so a reviewer can open the workbook and look — and whoever approves them cannot be you.'
                  : 'The record will say these numbers were keyed twice and agreed.'}
              </p>
              {/* When these limits start binding. It defaulted to today and the
                  screen never said so, which is right only when the workbook is
                  imported the day it was agreed — and a migration almost never
                  is. Today is still the default, because it is usually right for
                  a first load; the point is that it can now be seen and
                  changed. */}
              <label className="stacked">
                These limits take effect from
                <input
                  type="date"
                  data-testid="effective-from"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  title="The date the mandate says these limits start binding — not the date you are importing them."
                />
              </label>
              <p className="note">
                Not the date of the import. If this workbook records limits
                agreed in April, put April here, or a reviewer looking back will
                be told they started today.
              </p>
            </>
          )}
          {step === 'stage' && staged && (
            <>
              <p className="done" data-testid="staged">
                {staged}
              </p>
              {/* §7.1: whether a candidate amends an active limit or joins it is
                  answered in the amending document, not in the data — so these
                  are asked rather than guessed. One of them used to abort the
                  whole import; now the file lands and the questions are listed. */}
              {dropped.length > 0 && (
                <section className="warning" data-testid="not-in-file">
                  <h3>{dropped.length} limit(s) in force are not in this file</h3>
                  <p>
                    They stay in force. If the client has dropped them, open the fund on
                    Lookup and choose <strong>Propose to retire</strong> on each; an approver
                    then decides.
                  </p>
                  <table className="grid">
                    <thead>
                      <tr>
                        <DeskHeader
                          sort={droppedSort.sort}
                          onSort={droppedSort.onSort}
                          value={droppedDesk}
                          onChange={setDroppedDesk}
                        />
                        <th>Fund</th>
                        <th>Limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortRows(
                        dropped.filter((row) => !droppedDesk || directory.fund(row.portfolio_code) === droppedDesk),
                        droppedSort.sort,
                        { desk: (row) => directory.fund(row.portfolio_code) ?? '' },
                      ).map((row) => (
                        <tr key={row.rule_id}>
                          <DeskCell fund={row.portfolio_code} />
                          <td>{row.portfolio_code}</td>
                          <td>
                            <code>{row.rule_key}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
              {undecided.length > 0 && (
                <section className="warning" data-testid="needs-decision">
                  <h3>{undecided.length} row(s) held back</h3>
                  <p>
                    Each is listed with what stands in its way. A row dated on or
                    before the limit it replaces takes effect after that limit, so
                    import the workbook again with a later effective date. A row whose
                    limit already has a different proposal waiting is imported again once
                    that proposal is decided. Everything else in the file has been staged.
                  </p>
                  <table className="grid">
                    <thead>
                      <tr>
                        <th>Cell</th>
                        <th>Limit</th>
                        <th>What is in the way</th>
                      </tr>
                    </thead>
                    <tbody>
                      {undecided.map((row) => (
                        <tr key={row.ref}>
                          <td>
                            <code>{row.ref}</code>
                          </td>
                          <td>
                            <code>{row.rule_key}</code>
                          </td>
                          <td>{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          )}
        </section>
      )}

      {/* One way forward, and always a way back. `Next` is not permission to
          skip anything: staging is still refused without a clean comparison,
          and this only walks somebody through the order the work happens in.

          On the last step the thing that moves you forward *is* handing the work
          over, so it takes the position `Next` has held on every step before it.
          A dead `Next →` beside a live button somewhere up the page asks the
          reader to notice that the wizard has stopped being a wizard. */}
      <div className="wizard-nav">
        <button
          className="ghost"
          disabled={at === 0}
          onClick={() => setStep(STEPS[Math.max(0, at - 1)]!.id)}
        >
          ← Back
        </button>
        <span className="note">
          Step {at + 1} of {STEPS.length}
          {blocked && <> — {blocked}</>}
        </span>
        {step === 'stage' ? (
          /* Disabled until a reconciliation has run clean. The API refuses too;
             this is the same refusal where the operator can see it. */
          <button
            data-testid="stage"
            disabled={Boolean(staged) || (!readFromFile && stage !== 'reconciled')}
            onClick={() => void doStage()}
          >
            {staged ? 'Handed over' : 'Hand these to an approver'}
          </button>
        ) : (
          <button disabled={blocked !== null} onClick={forward}>
            Next →
          </button>
        )}
      </div>
    </div>
  )
}
